import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { LotProvider } from './contexts/LotContext';
import LotLoader from './components/LotLoader';
import LotsPage from './components/LotsPage';
import ParkingForm from './components/ParkingForm';
import PaymentPage from './components/PaymentPage';
import PaymentComplete from './components/PaymentComplete';
import ExtendTime from './components/ExtendTime';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import AdminSettings from './components/AdminSettings';
import UserManagement from './components/UserManagement';
import LotSelection from './components/LotSelection';
import ProtectedRoute from './components/ProtectedRoute';
import LotOwnerSignup from './components/LotOwnerSignup';
import OnboardingComplete from './components/OnboardingComplete';
import ResetPassword from './components/ResetPassword';
import ForcePasswordChange from './components/ForcePasswordChange';
import MarketingHome from './components/marketing/MarketingHome';

// Helper component for redirecting legacy /payment/:sessionId
const LegacyPaymentRedirect: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  return <Navigate to={`/lots/aberdeen-street/payment/${sessionId}`} replace />;
};

// Helper component for redirecting legacy /payment/complete/:sessionId
const LegacyPaymentCompleteRedirect: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  return <Navigate to={`/lots/aberdeen-street/payment/complete/${sessionId}`} replace />;
};

// Helper component for redirecting legacy /extend/:sessionId
const LegacyExtendRedirect: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  return <Navigate to={`/lots/aberdeen-street/extend/${sessionId}`} replace />;
};

// Lot-aware wrapper component for customer routes
const LotRoutes: React.FC = () => {
  return (
    <LotProvider>
      <Routes>
        <Route path="/" element={<ParkingForm />} />
        <Route path="/payment/:sessionId" element={<PaymentPage />} />
        <Route path="/payment/complete/:sessionId" element={<PaymentComplete />} />
        <Route path="/extend/:sessionId" element={<ExtendTime />} />
      </Routes>
    </LotProvider>
  );
};

// Lot-aware wrapper component for admin routes
const AdminLotRoutes: React.FC = () => {
  return (
    <LotProvider>
      <Routes>
        <Route path="/" element={<Navigate to="dashboard" replace />} />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <AdminSettings />
          </ProtectedRoute>
        } />
        <Route path="/users" element={
          <ProtectedRoute>
            <UserManagement />
          </ProtectedRoute>
        } />
      </Routes>
    </LotProvider>
  );
};

function App() {
  // This component inspects the initial URL for Supabase recovery links
  const RecoveryRedirect: React.FC = () => {
    const location = useLocation();
    useEffect(() => {
      // Supabase recovery links arrive as hash fragments anywhere
      // Example: #access_token=...&refresh_token=...&type=recovery
      if (location.hash && location.hash.includes('type=recovery')) {
        // Redirect to the reset-password route unless we're already there
        if (location.pathname !== '/admin/reset-password') {
          const hash = location.hash; // Preserve tokens
          window.history.replaceState({}, '', `/admin/reset-password${hash}`);
        }
      }
    }, [location]);
    return null;
  };

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <RecoveryRedirect />
        <Routes>
          {/* ParkConfetti marketing homepage */}
          <Route path="/" element={<MarketingHome />} />
          
          {/* Lots listing page (for drivers arriving via QR) */}
          <Route path="/lots" element={<LotsPage />} />
          
          {/* Lot Owner Onboarding */}
          <Route path="/signup/lot-owner" element={<LotOwnerSignup />} />
          <Route path="/onboarding/complete/:lotId" element={<OnboardingComplete />} />
          <Route path="/onboarding/refresh/:lotId" element={<OnboardingComplete />} />
          <Route path="/onboarding/stripe-complete/:lotId" element={<OnboardingComplete />} />
          
          {/* Multi-lot routes - wrapped with LotProvider */}
          <Route path="/lots/:lotSlug/*" element={<LotRoutes />} />
          
          {/* Backward compatibility redirects */}
          <Route path="/louisa" element={<Navigate to="/lots/louisa-street" replace />} />
          <Route path="/adeline" element={<Navigate to="/lots/aberdeen-street" replace />} />
          <Route path="/lots/adeline-street" element={<Navigate to="/lots/aberdeen-street" replace />} />
          
          {/* Legacy payment routes - redirect to lot-specific routes */}
          <Route path="/payment/:sessionId" element={<LegacyPaymentRedirect />} />
          <Route path="/payment/complete/:sessionId" element={<LegacyPaymentCompleteRedirect />} />
          <Route path="/extend/:sessionId" element={<LegacyExtendRedirect />} />
          
          {/* Admin routes - global (not lot-specific) */}
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/reset-password" element={<ResetPassword />} />
          <Route path="/admin/force-password-change" element={<ForcePasswordChange />} />
          <Route
            path="/admin/lot-selection"
            element={
              <ProtectedRoute>
                <LotSelection />
              </ProtectedRoute>
            }
          />
          
          {/* Lot-specific admin routes - shared LotProvider */}
          <Route path="/admin/lots/:lotSlug/*" element={<AdminLotRoutes />} />
          
          {/* 404 fallback */}
          <Route path="*" element={<Navigate to="/lots" replace />} />
        </Routes>
        
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
          }}
        />
      </div>
    </Router>
  );
}

export default App;